import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Unsaved choices in the Connection setup (QA U2): once someone types a name or changes a format,
 * a collection or a rule, reloading or closing the tab asks first. Saving, publishing or leaving the
 * setup (it unmounts) takes the warning away. Other agent types don't provide it, so nothing changes there.
 */
const ConnectionUnsavedContext = createContext({ markDirty: () => {}, markSaved: () => {} });

export const ConnectionUnsavedProvider = ConnectionUnsavedContext.Provider;

/** For the create flow: the warning while `enabled` (a Connection agent) and something is unsaved. */
export const useConnectionUnsavedGuard = enabled => {
  const [isDirty, setIsDirty] = useState(false);
  const markDirty = useCallback(() => setIsDirty(true), []);
  const markSaved = useCallback(() => setIsDirty(false), []);

  // Another agent type picked: nothing of the Connection setup is left to lose.
  useEffect(() => {
    if (!enabled) setIsDirty(false);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !isDirty) return undefined;
    const warn = event => {
      event.preventDefault();
      // Older browsers need a return value to show their "Leave site?" prompt.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [enabled, isDirty]);

  return useMemo(() => ({ markDirty, markSaved }), [markDirty, markSaved]);
};

export const useConnectionUnsaved = () => useContext(ConnectionUnsavedContext);

/** `dispatch` for someone's own choices in the setup: each one counts as unsaved. */
export const useConnectionChoiceDispatch = () => {
  const dispatch = useDispatch();
  const { markDirty } = useConnectionUnsaved();
  return useCallback(
    action => {
      markDirty();
      return dispatch(action);
    },
    [dispatch, markDirty]
  );
};
